import { IEdoQuestionAnswer, defaultValue } from "@/shared/model/edo-question-answer.model";
import { IEdoQuestionChoice } from "@/shared/model/edo-question-choice.model";
import { IEdoQuizAttempt } from "@/shared/model/edo-quiz-attempt.model";
import { IEdoQuizQuestion } from "@/shared/model/edo-quiz-question.model";
import { EdoQuestionTypeEnum } from "@/shared/model/enumerations/edo-question-type.mode";
import { getQuestionType } from "@/shared/util/question-utils";
import { cn } from "@/shared/util/cn";
import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { Translate, translate } from "react-jhipster";

interface ICourseQuestionProps {
    quizQuestion: IEdoQuizQuestion;
    quizAttempt: IEdoQuizAttempt;
    className?: string;
}

const textareaClasses = cn(
    "flex min-h-[100px] w-full rounded-2xl border border-[var(--color-mockup-border)] bg-white px-3 py-2 text-sm",
    "placeholder:text-[var(--color-mockup-text-light)] focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
    "dark:border-[var(--color-mockup-border)] dark:bg-[var(--color-surface-secondary)] dark:placeholder:text-[var(--color-mockup-text-light)]"
);

const CourseQuestion = (props: ICourseQuestionProps) => {
    const [form, setForm] = useState<IEdoQuestionAnswer>({
        ...defaultValue,
        question: props.quizQuestion.question,
        quizAttempt: props.quizAttempt,
    });
    const [choices, setChoices] = useState<IEdoQuestionChoice[]>([]);
    const {
        quizQuestion: { question },
        quizAttempt,
        className,
    } = props;

    const isNew = form?.id === null || form?.id === undefined;
    const autoSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const autoSave = (change: IEdoQuestionAnswer) => {
        if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
        autoSaveTimer.current = setTimeout(() => saveAnswer(change), 3000);
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const change = {
            ...form,
            [event.target.name]:
                event.target.type === "checkbox"
                    ? (event.target as HTMLInputElement).checked
                    : event.target.value,
        };
        autoSave(change);
        setForm(change);
    };

    const getUserAnswer = async () => {
        const response = await axios.get(
            `/quiz-attempts/${quizAttempt.id}/questions/${question.id}/answer`
        );
        if (response.data.data) setForm(response.data.data);
    };

    const saveAnswer = async (change: IEdoQuestionAnswer) => {
        if (isNew) {
            const response = await axios.post(`question-answers`, change);
            setForm(response.data.data);
        } else {
            const response = await axios.put(`question-answers/${change.id}`, change);
            setForm(response.data.data);
        }
    };

    const handleGetChoices = async () => {
        const response = await axios.get<{ data: IEdoQuestionChoice[] }>(
            `/questions/${question.id}/choices`
        );
        setChoices(response.data.data);
    };

    useEffect(() => {
        if (
            quizAttempt?.courseQuiz?.id &&
            (question.type === EdoQuestionTypeEnum.SINGLE_CHOICE ||
                question.type === EdoQuestionTypeEnum.TRUE_FALSE ||
                question.type === EdoQuestionTypeEnum.MULTIPLE_CHOICE ||
                question.type === EdoQuestionTypeEnum.FILL_BLANK)
        ) {
            handleGetChoices();
        }
        getUserAnswer();
    }, [question, quizAttempt?.courseQuiz?.id]);

    const getMultipleChoiceOptions = () => {
        if (!choices?.length) {
            return (
                <div className="py-4 text-center text-body text-[var(--color-mockup-text-mid)]">
                    <Translate contentKey="edocendoApp.edoQuestionChoice.home.notFound">
                        No Edo Question Choices found
                    </Translate>
                </div>
            );
        }

        const isSingle =
            question.type === EdoQuestionTypeEnum.SINGLE_CHOICE ||
            question.type === EdoQuestionTypeEnum.TRUE_FALSE;

        return (
            <fieldset className="mt-4">
                <legend className="mb-2 text-body font-medium text-[var(--color-mockup-text)]">
                    Risposte disponibili
                </legend>
                <div className="space-y-2">
                    {choices.map((choice) => {
                        const isChecked =
                            form?.choices?.findIndex((c) => c.id === choice.id) !== -1;
                        return (
                            <label
                                key={choice.id}
                                className="flex cursor-pointer items-start gap-3 rounded-2xl border border-[var(--color-mockup-border)] px-3 py-2 transition-colors hover:bg-[var(--color-surface-secondary)] dark:border-[var(--color-mockup-border)] dark:hover:bg-primary/15"
                            >
                                <input
                                    type={isSingle ? "radio" : "checkbox"}
                                    name={isSingle ? "question-choices-group" : undefined}
                                    checked={isChecked}
                                    onChange={() => {
                                        if (isSingle) {
                                            const change = { ...form, choices: [choice] };
                                            saveAnswer(change);
                                            setForm(change);
                                        } else {
                                            let choicesCopy = Array.from(form?.choices ?? []);
                                            if (isChecked) {
                                                choicesCopy = choicesCopy.filter(
                                                    (c) => c.id !== choice.id
                                                );
                                            } else {
                                                choicesCopy.push(choice);
                                            }
                                            const change = { ...form, choices: choicesCopy };
                                            saveAnswer(change);
                                            setForm(change);
                                        }
                                    }}
                                    className="mt-1 h-4 w-4 shrink-0 border-[var(--color-mockup-border)] text-primary focus:ring-primary dark:border-[var(--color-mockup-border)]"
                                />
                                <span className="text-body text-[var(--color-mockup-text)]">
                                    {choice.description}
                                </span>
                            </label>
                        );
                    })}
                </div>
            </fieldset>
        );
    };

    return (
        <div
            className={cn(
                "surface surface-hover overflow-hidden rounded-3xl p-6 transition-all duration-200",
                className
            )}
        >
            <div className="mb-4">
                <p className="text-body text-[var(--color-mockup-text)]">
                    {question.description}
                </p>
                <p className="mt-1 text-body-sm text-[var(--color-mockup-text-mid)]">
                    {getQuestionType(question.type)}
                </p>
            </div>

            <div className="space-y-4">
                {question.type === EdoQuestionTypeEnum.FREE_TEXT && (
                    <div>
                        <label className="mb-1 block text-body font-medium text-[var(--color-mockup-text-mid)]">
                            {translate("edocendoApp.edoQuestionAnswer.description")}
                        </label>
                        <textarea
                            name="description"
                            rows={4}
                            value={form?.description ?? ""}
                            onChange={handleChange}
                            className={textareaClasses}
                            placeholder={translate("edocendoApp.edoQuestionAnswer.description")}
                        />
                    </div>
                )}
                {(question.type === EdoQuestionTypeEnum.SINGLE_CHOICE ||
                    question.type === EdoQuestionTypeEnum.TRUE_FALSE ||
                    question.type === EdoQuestionTypeEnum.MULTIPLE_CHOICE ||
                    question.type === EdoQuestionTypeEnum.FILL_BLANK) &&
                    getMultipleChoiceOptions()}
                {question.explanationRequired && (
                    <div>
                        <label className="mb-1 block text-body font-medium text-[var(--color-mockup-text-mid)]">
                            {translate("edocendoApp.edoQuestionAnswer.explanation")}
                        </label>
                        <textarea
                            name="explanation"
                            rows={4}
                            value={form?.explanation ?? ""}
                            onChange={handleChange}
                            className={textareaClasses}
                            placeholder={translate("edocendoApp.edoQuestionAnswer.explanation")}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default CourseQuestion;
